import React, { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Bot, User, RefreshCw, Trophy } from "lucide-react";
import GameOptions from "./GameOptions";
import { useGameBot } from "./useGameBot";
import PlayerNameInput from "./PlayerNameInput";

const SimGame = ({ onBack }) => {
  const navigate = useNavigate();

  // Game State
  // adjacency matrix 6x6. 0=none, 1=P1, 2=P2
  const [board, setBoard] = useState(
    Array(6).fill(null).map(() => Array(6).fill(0))
  );
  
  const [gameStarted, setGameStarted] = useState(false);
  const [currentPlayer, setCurrentPlayer] = useState(1);
  const [gameOver, setGameOver] = useState(false);
  const [winner, setWinner] = useState(null);

  // Settings
  const [gameMode, setGameMode] = useState("PvP");
  const [difficulty, setDifficulty] = useState("Easy");
  const [player1Name, setPlayer1Name] = useState("Player 1");
  const [player2Name, setPlayer2Name] = useState("Player 2");

  const getPlayerName = (id) => {
    if (gameMode === "PvC" && id === 2) return "Computer";
    return id === 1 ? player1Name : player2Name;
  };

  const startGame = () => {
    setBoard(Array(6).fill(null).map(() => Array(6).fill(0)));
    setCurrentPlayer(1);
    setGameOver(false);
    setWinner(null);
    setGameStarted(true);
  };

  // Logic: Check if player forming edge (u, v) creates a monochromatic triangle
  // Returns true if LOSS
  const causesLoss = (currentBoard, u, v, player) => {
      // Check for any node 'k' such that (u,k) and (v,k) are already owned by player
      for (let k = 0; k < 6; k++) {
          if (k === u || k === v) continue;
          if (currentBoard[u][k] === player && currentBoard[v][k] === player) {
              return true; // Triangle (u, v, k) formed!
          }
      }
      return false;
  };

  const handleMove = useCallback((u, v) => {
      return (currentBoard, currentTurn) => {
          if (currentBoard[u][v] !== 0) return null; // Invalid

          const newBoard = currentBoard.map(row => [...row]);
          newBoard[u][v] = currentTurn;
          newBoard[v][u] = currentTurn;

          // Check loss immediately
          // Note: In Sim, the player who completes the triangle LOSES.
          if (causesLoss(currentBoard, u, v, currentTurn)) {
              // Current player loses, so OTHER player wins
              const winnerId = currentTurn === 1 ? 2 : 1;
              return {
                  newBoard,
                  gameOver: true,
                  winner: winnerId,
                  nextTurn: currentTurn // Doesn't matter
              };
          }

          return {
              newBoard,
              gameOver: false,
              winner: null,
              nextTurn: currentTurn === 1 ? 2 : 1
          };
      };
  }, []);

  const executeMove = (u, v) => {
      if (gameOver || !gameStarted) return;
      
      const logic = handleMove(u, v);
      const result = logic(board, currentPlayer);
      
      if (!result) return;

      setBoard(result.newBoard);
      
      if (result.gameOver) {
          setGameOver(true);
          setWinner(result.winner);
      } else {
          setCurrentPlayer(result.nextTurn);
      }
  };

  // AI Logic
  const getBotMove = useCallback((diff, gameState) => {
      const { board, currentPlayer } = gameState;
      
      // Get all available moves
      const availableMoves = [];
      for (let i = 0; i < 6; i++) {
          for (let j = i + 1; j < 6; j++) {
              if (board[i][j] === 0) {
                  availableMoves.push({ u: i, v: j });
              }
          }
      }

      if (availableMoves.length === 0) return null; // Should not happen in Sim as draw is impossible

      // Filter moves that cause immediate loss (Suicide moves)
      const safeMoves = availableMoves.filter(m => !causesLoss(board, m.u, m.v, currentPlayer));

      // Easy: Random move (might suicide)
      if (diff === "Easy") {
          // 20% chance to pick purely random, else try to pick safe
          if (Math.random() < 0.2) {
             return availableMoves[Math.floor(Math.random() * availableMoves.length)]; 
          }
      }

      // Medium/Hard: Always pick safe move if available
      if (safeMoves.length > 0) {
           return safeMoves[Math.floor(Math.random() * safeMoves.length)];
      }

      // If only suicide moves left, we must pick one (and lose)
      return availableMoves[0];

  }, []);

  const { isBotThinking } = useGameBot({
    gameMode,
    makeMove: ({u, v}) => executeMove(u, v),
    playerTurn: currentPlayer,
    gameStarted,
    gameOver,
    botStrategy: getBotMove,
    gameState: { board, currentPlayer },
    difficulty
  });


  // Rendering Helpers
  const radius = 140;
  const center = { x: 200, y: 200 };
  const nodes = Array.from({ length: 6 }).map((_, i) => {
      const angle = (i * 60 - 90) * (Math.PI / 180);
      return {
          x: center.x + radius * Math.cos(angle),
          y: center.y + radius * Math.sin(angle),
          id: i
      };
  });

  return (
    <div className="max-w-4xl mx-auto p-4">
      <div className="mb-4">
        <button
          onClick={() => (onBack ? onBack() : navigate("/games"))}
          className="flex items-center text-blue-600 hover:text-blue-800 font-medium"
        >
          ← Back to Games
        </button>
      </div>

      <div className="bg-white rounded-lg shadow-xl p-8">
        <h1 className="text-3xl font-bold text-center mb-2 text-gray-800">Sim</h1>
        <p className="text-center text-gray-600 mb-8">
             Avoid creating a triangle of your own color!
        </p>

        {!gameStarted ? (
           <div className="text-center animate-fade-in">
             <GameOptions
               gameMode={gameMode}
               setGameMode={setGameMode}
               difficulty={difficulty}
               setDifficulty={setDifficulty}
               onStart={startGame}
             />
             <PlayerNameInput
                 player1Name={player1Name}
                 setPlayer1Name={setPlayer1Name}
                 player2Name={player2Name}
                 setPlayer2Name={setPlayer2Name}
                 gameMode={gameMode}
             />
             <div className="mt-8 text-gray-600 max-w-2xl mx-auto">
               <h3 className="font-semibold mb-4 text-lg">Game Rules:</h3>
               <div className="bg-gray-50 p-4 rounded-lg text-left space-y-2">
                 <p>• <strong>Goal:</strong> Force your opponent to form a triangle of their own color.</p>
                 <p>• <strong>Gameplay:</strong> 6 points are arranged in a hexagon. All points are connected to each other.</p>
                 <p>• <strong>Turn:</strong> Players take turns coloring a line (edge) connecting two points.</p>
                 <p>• <strong>Losing Condition:</strong> The first player to complete a triangle (connecting 3 points) using ONLY their color LOSES immediately.</p>
               </div>
             </div>
           </div>
        ) : (
           <div className="animate-fade-in">
               
               {/* Status Header */}
               <div className="flex justify-between items-center mb-6 px-4 md:px-12">
                    <div className={`text-center px-6 py-3 rounded-lg border-2 ${currentPlayer === 1 ? 'border-blue-500 bg-blue-50' : 'border-transparent'}`}>
                        <div className="text-lg font-bold text-blue-700 flex items-center gap-2">
                             <User className="w-5 h-5" /> {getPlayerName(1)}
                        </div>
                    </div>
                    
                    <div className="text-xl font-bold text-gray-400">VS</div>

                    <div className={`text-center px-6 py-3 rounded-lg border-2 ${currentPlayer === 2 ? 'border-red-500 bg-red-50' : 'border-transparent'}`}>
                        <div className="text-lg font-bold text-red-700 flex items-center gap-2">
                             {gameMode === 'PvC' ? <Bot className="w-5 h-5" /> : <User className="w-5 h-5" />}
                             {getPlayerName(2)}
                        </div>
                    </div>
               </div>

               {/* Game Over Message */}
               {gameOver && (
                   <div className="mb-6 text-center animate-bounce-in">
                       <h2 className="text-3xl font-bold text-purple-600 flex items-center justify-center gap-3">
                           <Trophy className="w-8 h-8" />
                           {getPlayerName(winner)} Wins!
                       </h2>
                       <p className="text-gray-500 mt-2">
                           (The other player formed a triangle!)
                       </p>
                   </div>
               )}

               {isBotThinking && !gameOver && (
                   <div className="text-center text-purple-500 font-medium mb-4 animate-pulse">
                       Computer is thinking...
                   </div>
               )}

               {/* Board */}
               <div className="flex justify-center mb-8">
                   <div className="relative select-none">
                       <svg width="400" height="400" className="bg-gray-50 rounded-full shadow-inner border border-gray-200">
                           {/* Draw all Lines */}
                           {nodes.map((node1, i) => (
                               nodes.slice(i + 1).map((node2, j) => {
                                   const targetIndex = i + 1 + j; // Correct index from slice offset
                                   const owner = board[i][targetIndex];
                                   
                                   // Interaction handlers
                                   const isClickable = !gameOver && !isBotThinking && owner === 0;
                                   const strokeColor = owner === 1 ? "#3b82f6" : (owner === 2 ? "#ef4444" : "#e5e7eb"); // Blue, Red, Gray-200
                                   const strokeWidth = owner !== 0 ? 4 : 2;
                                   
                                   return (
                                       <g key={`${i}-${targetIndex}`} 
                                          onClick={() => isClickable && executeMove(i, targetIndex)}
                                          className={isClickable ? "cursor-pointer group" : ""}
                                       >
                                           {/* Invisible thick line for easier clicking */}
                                           <line
                                               x1={node1.x} y1={node1.y}
                                               x2={node2.x} y2={node2.y}
                                               stroke="transparent"
                                               strokeWidth="20"
                                           />
                                           {/* Visible Line */}
                                           <line
                                               x1={node1.x} y1={node1.y}
                                               x2={node2.x} y2={node2.y}
                                               stroke={strokeColor}
                                               strokeWidth={strokeWidth}
                                               strokeLinecap="round"
                                               className={`transition-colors duration-300 ${isClickable ? 'group-hover:stroke-gray-400' : ''}`}
                                           />
                                       </g>
                                   );
                               })
                           ))}

                           {/* Draw Nodes */}
                           {nodes.map((node) => (
                               <g key={node.id}>
                                   <circle
                                       cx={node.x}
                                       cy={node.y}
                                       r="8"
                                       className="fill-gray-800"
                                   />
                                   {/* Debug indices 
                                   <text x={node.x} y={node.y - 15} textAnchor="middle">{node.id}</text> 
                                   */}
                               </g>
                           ))}
                       </svg>
                   </div>
               </div>

                <div className="flex justify-center">
                    <button 
                        onClick={() => setGameStarted(false)} 
                        className="flex items-center gap-2 px-6 py-2 bg-gray-200 text-gray-700 font-bold rounded-full hover:bg-gray-300 transition-colors shadow-sm"
                    >
                        <RefreshCw className="w-5 h-5" />
                        New Game
                    </button>
                </div>

           </div>
        )}
      </div>
    </div>
  );
};

export default SimGame;
